import {
  type ActionPlan,
  actionPlanOf,
  changeProductFor,
  decidableMultiProductOrderTreatmentIds,
  type FamilyDecision,
  familyDecisionTargetsOf,
  pendingMultiProductOrderFamilyRows
} from '@/lib/med-requests'
import type { MultiProductOrderTreatment, TreatmentDecision } from '@/lib/stores/medication-request'
import { convertDateToISOWithoutConverting, withFutureDateNote } from '@/lib/utils'
import type { AdditionalNotesType, Medication, MedRequest } from '@/types/data'
import type { MedRequestMutationFnProps } from '../action'

/**
 * The page's notes for another item's action: without the CS note (see withPageCsNote), and with
 * the future date line following the page's issue date.
 */
const familyNotesOf = (notes: AdditionalNotesType | undefined, issueDate?: Date): AdditionalNotesType | undefined =>
  notes && { ...notes, customerService: '', pharmacist: withFutureDateNote(notes.pharmacist, issueDate) }

/**
 * Sends the page's CS note once: on the first action when the opened request's own isn't among
 * them, unless the opened request already holds it.
 */
export const withPageCsNote = (
  medRequest: MedRequest,
  notes: AdditionalNotesType | undefined,
  actions: MedRequestMutationFnProps[]
): MedRequestMutationFnProps[] => {
  const customerService = notes?.customerService
  const [lead, ...rest] = actions
  if (
    !lead ||
    actions.some(action => action.id === medRequest.id) ||
    !customerService?.trim() ||
    customerService === medRequest.additionalNotes?.customerService
  ) {
    return actions
  }

  return [{ ...lead, additionalNotes: { ...lead.additionalNotes, customerService } }, ...rest]
}

/** A card is approvable once it has a medication and directions. */
export const isMultiProductOrderTreatmentComplete = (
  treatment: Pick<MultiProductOrderTreatment, 'medication' | 'direction'> | null | undefined
) => !!treatment?.medication && !!treatment.direction.trim()

/** The pending rows' cards; null (incomplete) for a row whose card hasn't seeded yet. */
export const pendingMultiProductOrderFamilyTreatments = (
  medRequest: MedRequest,
  treatments: readonly MultiProductOrderTreatment[]
): Array<MultiProductOrderTreatment | null> =>
  pendingMultiProductOrderFamilyRows(medRequest).map(
    row => treatments.find(treatment => treatment.id === row.id) ?? null
  )

type FamilyApproveContext = { reason: string; notes: AdditionalNotesType; issueDate: Date }

/** Another item's approve, from its own card plus the page's notes and issue date. */
const familyApproveAction = (
  row: MedRequest,
  treatment: MultiProductOrderTreatment,
  { reason, notes, issueDate }: FamilyApproveContext
): MedRequestMutationFnProps => ({
  id: row.id,
  action: 'approve',
  reason,
  approveDate: convertDateToISOWithoutConverting(issueDate),
  extraDirectionsForMedication: treatment.direction || undefined,
  changeProduct: changeProductFor(row, treatment.medication),
  additionalNotes: familyNotesOf(notes, issueDate)
})

/**
 * Approve all's actions for the other pending items. A row whose card is missing or incomplete is
 * left out rather than approved on the ordered item's defaults.
 */
export const multiProductOrderFamilyApproveActions = (
  medRequest: MedRequest,
  { treatments, ...context }: FamilyApproveContext & { treatments: readonly MultiProductOrderTreatment[] }
): MedRequestMutationFnProps[] =>
  pendingMultiProductOrderFamilyRows(medRequest).flatMap(row => {
    const treatment = treatments.find(entry => entry.id === row.id)
    if (!treatment || !isMultiProductOrderTreatmentComplete(treatment)) return []

    return [familyApproveAction(row, treatment, context)]
  })

/**
 * Reject all / Follow Up: the opened request's action, then the same for each other target
 * (suppressCsEmailForMps, so CS gets one task). If the opened request isn't a target, the first
 * other one carries the conversation, CS contact and CS note instead.
 */
const withMultiProductOrderFamilyDecisions = (
  medRequest: MedRequest,
  openedAction: MedRequestMutationFnProps & { action: FamilyDecision }
): MedRequestMutationFnProps[] => {
  const reason = openedAction.reason ?? ''
  const { isOpenedTarget, rows } = familyDecisionTargetsOf(medRequest, openedAction.action)
  const family = rows.map(row => ({
    id: row.id,
    action: openedAction.action,
    reason,
    additionalNotes: familyNotesOf(openedAction.additionalNotes),
    suppressCsEmailForMps: true
  }))

  if (isOpenedTarget) return [openedAction, ...family]

  const [lead, ...rest] = family
  if (!lead) return []

  return withPageCsNote(medRequest, openedAction.additionalNotes, [
    {
      ...lead,
      startConversation: openedAction.startConversation,
      csNeedToContactPatient: openedAction.csNeedToContactPatient,
      suppressCsEmailForMps: false
    },
    ...rest
  ])
}

export const multiProductOrderDecisionPlan = (
  medRequest: MedRequest,
  openedAction: MedRequestMutationFnProps & { action: FamilyDecision }
): ActionPlan => actionPlanOf(withMultiProductOrderFamilyDecisions(medRequest, openedAction), openedAction)

/** What the Clinical decision panel collects for one treatment. */
export type TreatmentDecisionInput = Omit<TreatmentDecision, 'action'>

/** The page's state for the opened request — Treatment 1 lives on the store's flat fields. */
export type OpenedTreatmentState = {
  medication: Medication | null
  medicationDirection: string
  medicationIssueDate: Date
  notes: AdditionalNotesType
  scrExecutedPrompt?: string
  scrSummaryResult?: string
}

/** The opened request's approve, from the page's state. */
export const openedApproveAction = (
  medRequest: MedRequest,
  opened: OpenedTreatmentState,
  reason: string
): MedRequestMutationFnProps => ({
  id: medRequest.id,
  action: 'approve',
  reason,
  approveDate: convertDateToISOWithoutConverting(opened.medicationIssueDate),
  additionalNotes: opened.notes,
  extraDirectionsForMedication: opened.medicationDirection,
  changeProduct: changeProductFor(medRequest, opened.medication),
  scrExecutedPrompt: opened.scrExecutedPrompt,
  scrSummaryResult: opened.scrSummaryResult
})

/** Submit decision's action for one recorded decision. Null if the treatment is gone or not approvable yet. */
const treatmentDecisionAction = (
  medRequest: MedRequest,
  treatmentId: string,
  decision: TreatmentDecision,
  { opened, treatments }: { opened: OpenedTreatmentState; treatments: readonly MultiProductOrderTreatment[] }
): MedRequestMutationFnProps | null => {
  const { action, startConversation, csNeedToContactPatient } = decision
  const reason = decision.reason.trim()

  if (treatmentId === medRequest.id) {
    if (action === 'approve') {
      const isComplete = isMultiProductOrderTreatmentComplete({
        medication: opened.medication,
        direction: opened.medicationDirection
      })
      return isComplete ? openedApproveAction(medRequest, opened, reason) : null
    }

    return {
      id: medRequest.id,
      action: 'reject',
      reason,
      startConversation,
      csNeedToContactPatient,
      additionalNotes: opened.notes,
      extraDirectionsForMedication: opened.medicationDirection,
      scrExecutedPrompt: opened.scrExecutedPrompt,
      scrSummaryResult: opened.scrSummaryResult
    }
  }

  const row = (medRequest.orderMedRequests ?? []).find(entry => entry.id === treatmentId)
  if (!row) return null

  const treatment = treatments.find(entry => entry.id === treatmentId)

  if (action === 'approve') {
    if (!treatment || !isMultiProductOrderTreatmentComplete(treatment)) return null
    return familyApproveAction(row, treatment, { reason, notes: opened.notes, issueDate: opened.medicationIssueDate })
  }

  return {
    id: row.id,
    action: 'reject',
    reason,
    startConversation,
    csNeedToContactPatient,
    extraDirectionsForMedication: treatment?.direction || undefined,
    additionalNotes: familyNotesOf(opened.notes)
  }
}

/**
 * Submit decision's actions, in card order. CS contact stays on the first rejection asking for it,
 * so one submit raises a single CS task. Null when an action can't be built.
 */
export const multiProductOrderTreatmentDecisionActions = (
  medRequest: MedRequest,
  decisions: Readonly<Record<string, TreatmentDecision>>,
  state: { opened: OpenedTreatmentState; treatments: readonly MultiProductOrderTreatment[] }
): MedRequestMutationFnProps[] | null => {
  const actions: MedRequestMutationFnProps[] = []

  for (const treatmentId of decidableMultiProductOrderTreatmentIds(medRequest)) {
    const decision = decisions[treatmentId]
    if (!decision) continue

    const action = treatmentDecisionAction(medRequest, treatmentId, decision, state)
    if (!action) return null
    actions.push(action)
  }

  const csContactIndex = actions.findIndex(action => action.csNeedToContactPatient)
  const withSingleCsContact = actions.map((action, index) =>
    index > csContactIndex && action.csNeedToContactPatient ? { ...action, csNeedToContactPatient: false } : action
  )

  return withPageCsNote(medRequest, state.opened.notes, withSingleCsContact)
}
